<script setup lang="ts" generic="ColumnId extends string">
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import type { CheckBoxeType } from '#components/elements/checkBox/CheckBoxGroup.vue';
import PopUp from '#components/popUp/PopUp.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import PerimeterReminder from '#domain/performance/kpi/components/KpiDataTable/ConfigurationPopIn/components/PerimeterReminder.vue';
import {
  type DataTableColumn,
  type DataTableDimensionsType,
  type DataTableKpiDisplayNameType,
  type DataTableViewType
} from '@carrefour-gcs/shared';
import { computed, ref } from 'vue';
import DimensionsSelectorList from './components/DimensionsSelectorList.vue';
import KpiSelectorList from './components/KpiSelectorList.vue';

const {
  availableKpis,
  selectedKpis,
  availableDimensions,
  selectedDimensions,
  disabledDimensions,
  kpisColumnsOrder,
  dimensionsOrder,
  datatableView
} = defineProps<{
  isOpen: boolean;
  availableKpis: Pick<DataTableColumn, 'name' | 'label' | 'required'>[];
  availableDimensions: Pick<DataTableColumn, 'name' | 'label' | 'required'>[];
  selectedKpis: DataTableKpiDisplayNameType[];
  selectedDimensions: DataTableDimensionsType[];
  disabledDimensions: DataTableDimensionsType[];
  kpisColumnsOrder: DataTableKpiDisplayNameType[];
  dimensionsOrder: DataTableDimensionsType[];
  datatableView: DataTableViewType;
}>();

const isPerimeterSectionExpanded = ref(true);

const ConfigStep = {
  CONFIGURING: 'CONFIGURING',
  EDIT_PERIMETER_DIALOG: 'EDIT_PERIMETER_DIALOG'
} as const;

type ConfigStepType = (typeof ConfigStep)[keyof typeof ConfigStep];

const currentStep = ref<ConfigStepType>(ConfigStep.CONFIGURING);

const emits = defineEmits<{
  close: [];
  updateConfiguration: [
    {
      selectedKpis: DataTableKpiDisplayNameType[];
      selectedDimensions: DataTableDimensionsType[];
      kpisColumnsOrder: DataTableKpiDisplayNameType[];
      dimensionsOrder: DataTableDimensionsType[];
    },
    boolean
  ];
}>();
const { t } = useLanguage();

const {
  withTax: { isWithTax },
  isKpiFiltersPopinOpen
} = useKpisPerimeter();

const formKpisList = ref<CheckBoxeType[]>([]);
const formDimensionsList = ref<CheckBoxeType[]>([]);
const isFormValid = computed(() => {
  return !!formKpisList.value.find((option) => option.checked);
});

function applyConfiguration(withExport: boolean) {
  emits(
    'updateConfiguration',
    {
      selectedKpis: formKpisList.value
        .filter((option) => option.checked)
        .map((option) => option.id) as DataTableKpiDisplayNameType[],
      selectedDimensions: formDimensionsList.value
        .filter((option) => option.checked)
        .map((option) => option.id) as DataTableDimensionsType[],
      kpisColumnsOrder: formKpisList.value.map((option) => option.id) as DataTableKpiDisplayNameType[],
      dimensionsOrder: formDimensionsList.value.map((option) => option.id) as DataTableDimensionsType[]
    },
    withExport
  );
}

function editFilters() {
  isKpiFiltersPopinOpen.value = true;
  emits('close');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
</script>

<template>
  <PopUp
    :title="t('dataTableConfigurationPopIn.title')"
    title-size="medium"
    :title-centered="currentStep === ConfigStep.EDIT_PERIMETER_DIALOG"
    :vertical-position="currentStep === ConfigStep.EDIT_PERIMETER_DIALOG ? 'center' : 'top'"
    :open="isOpen"
    @on-close="emits('close')"
  >
    <div v-if="currentStep === ConfigStep.CONFIGURING" class="w-full">
      <div class="overflow-y-auto custom-vertical-scrollbar max-h-[65vh] pr-4 mr-4">
        <p class="mb-6">{{ t('dataTableConfigurationPopIn.subtitle') }}</p>

        <hr />
        <div class="my-4">
          <div class="flex items-center justify-between gap-4">
            <h4 class="text-xl text-primary font-bold">
              {{ t('dataTableConfigurationPopIn.perimeter.title') }}
            </h4>

            <div class="flex items-center">
              <ButtonIcon
                variant="primary"
                icon="edit"
                background="transparent"
                @click="currentStep = ConfigStep.EDIT_PERIMETER_DIALOG"
              />
              <ButtonIcon
                variant="primary"
                :icon="isPerimeterSectionExpanded ? 'chevron-up' : 'chevron-down'"
                background="transparent"
                @click="isPerimeterSectionExpanded = !isPerimeterSectionExpanded"
              />
            </div>
          </div>

          <div v-if="isPerimeterSectionExpanded" class="mt-2">
            <PerimeterReminder />
          </div>
        </div>

        <hr />
        <KpiSelectorList
          v-bind="{ availableKpis, selectedKpis, columnsOrder: kpisColumnsOrder, isWithTax }"
          @update:form-kpis-list="(value) => (formKpisList = value)"
        />
        <hr />
        <DimensionsSelectorList
          v-bind="{ datatableView, availableDimensions, selectedDimensions, disabledDimensions, dimensionsOrder }"
          @update:form-dimensions-list="(value) => (formDimensionsList = value)"
        />
        <hr />
      </div>

      <div class="flex items-center justify-end gap-4 mt-8">
        <ButtonBase
          size="large"
          icon-left="mail"
          :label="t('dataTableConfigurationPopIn.export')"
          variant="secondary"
          :disabled="!isFormValid"
          @click="applyConfiguration(true)"
        />
        <ButtonBase
          size="large"
          :label="t('dataTableConfigurationPopIn.apply')"
          variant="primary"
          :disabled="!isFormValid"
          @click="applyConfiguration(false)"
        />
      </div>
    </div>

    <div v-if="currentStep === ConfigStep.EDIT_PERIMETER_DIALOG">
      <div class="w-full text-center">
        <p>
          {{ t('dataTableConfigurationPopIn.editPerimerDialog.content') }}
        </p>
      </div>
      <div class="flex items-center gap-4 mt-8 w-full justify-center">
        <ButtonBase
          size="large"
          :label="t('dataTableConfigurationPopIn.backToConfiguration')"
          variant="secondary"
          @click="currentStep = ConfigStep.CONFIGURING"
        />
        <ButtonBase
          size="large"
          :label="t('dataTableConfigurationPopIn.confirmExit')"
          variant="primary"
          @click="editFilters"
        />
      </div>
    </div>
  </PopUp>
</template>
